import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GovLayout, GovLayoutColumn } from '@gov-design-system-ce/react';
import { useLanguageNamespaceLoader, useLocale } from '@gov-nx/core/service';
import {
	PageCode,
	SearchAllResultType,
	useHledaniContext,
} from '@gov-nx/module/page';
import {
	Breadcrumbs,
	Button,
	InfoBanner,
	LayoutGap,
	Tabs,
} from '@gov-nx/ui/web';
import type { PagePath } from '@gov-nx/web';
import {
	pageRouteResolver,
	scrollToTop,
	useSearchParamsOperations,
} from '@gov-nx/web';
import { SearchGridHeader } from './SearchGridHeader';
import { SearchResultsGrid } from './SearchResultsGrid';
import { Skeleton } from './Skeleton';
import { SortDropdown } from './SortDropdown';

export interface SearchResultsViewProps {
	code: PageCode;
	selectedTab?: SearchAllResultType;
}

export const SearchResultsView = ({
	code,
	selectedTab,
}: SearchResultsViewProps) => {
	const {
		results,
		searchTerm,
		setFilterTab,
		setFilterAll,
		numberOfResults,
		isFetching,
		filter,
		recommendedResults,
		canLoadMoreRecommended,
		loadMoreRecommended,
		loadMoreResults,
		isSortedAlphabetically,
	} = useHledaniContext();

	const { getParam } = useSearchParamsOperations();
	const q = getParam('q');

	const { t } = useLocale(code);
	const translations = useLanguageNamespaceLoader(code);
	const navigate = useNavigate();
	const searchedWord = `"${q}"`;

	useEffect(() => {
		searchTerm.update(q);
		scrollToTop();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [q]);

	useEffect(() => {
		if (selectedTab) {
			setFilterTab(selectedTab);
		} else {
			setFilterAll();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [selectedTab]);

	if (translations.isLoading) {
		return <Skeleton code={code} />;
	}

	return (
		<LayoutGap gap="l">
			<Breadcrumbs
				options={[
					{ label: t('domu'), toPage: 'dashboard' },
					{ label: t('vyhledavani') + ' ' + searchedWord },
				]}
			/>
			<h1 className="wrap-anywhere hyphens-auto">
				{t('vyhledavani')} {searchedWord}
			</h1>
			<GovLayout
				type="text"
				variant="left">
				<GovLayoutColumn>
					<Tabs
						items={[
							{
								label: t('tab.vsechny-kategorie'),
								triggerIdentifier: 'all',
								isActive: !(
									selectedTab === SearchAllResultType.Signpost ||
									selectedTab === SearchAllResultType.UsefulLink ||
									selectedTab === SearchAllResultType.Faq
								),
								children: null,
							},
							{
								label: t('tab.sluzby'),
								triggerIdentifier: SearchAllResultType.Signpost,
								isActive: selectedTab === SearchAllResultType.Signpost,
								children: null,
							},
							{
								label: t('tab.uzitecne-odkazy'),
								triggerIdentifier: SearchAllResultType.UsefulLink,
								isActive: selectedTab === SearchAllResultType.UsefulLink,
								children: null,
							},
							{
								label: t('tab.caste-dotazy'),
								triggerIdentifier: SearchAllResultType.Faq,
								isActive: selectedTab === SearchAllResultType.Faq,
								children: null,
							},
						]}
						onChange={(identifier) => {
							if (identifier === SearchAllResultType.Signpost) {
								navigate(
									pageRouteResolver(
										PageCode['vyhledavani-sluzby'] as PagePath,
										{
											q,
										}
									)
								);
							}
							if (identifier === SearchAllResultType.UsefulLink) {
								navigate(
									pageRouteResolver(
										PageCode['vyhledavani-uzitecne-odkazy'] as PagePath,
										{
											q,
										}
									)
								);
							}
							if (identifier === SearchAllResultType.Faq) {
								navigate(
									pageRouteResolver(
										PageCode['vyhledavani-caste-dotazy'] as PagePath,
										{
											q,
										}
									)
								);
							}
							if (identifier === 'all') {
								navigate(
									pageRouteResolver(
										PageCode['vysledky-vyhledavani'] as PagePath,
										{
											q,
										}
									)
								);
							}
						}}
					/>
					<LayoutGap
						gap="xl"
						className="items-center">
						{!isFetching && numberOfResults === 0 && (
							<>
								<InfoBanner
									orientation="horizontal"
									headline={t('stav.chyba.nebyli-nalezeny-vysledky', {
										searchedWord,
									})}
									text={t('stav.chyba.opravte-preklepy')}
									className="gov-empty--white !flex-row !items-center"
								/>
								<SearchGridHeader>
									<p>{t('ostatni-navstevuji')}</p>
								</SearchGridHeader>
								<SearchResultsGrid
									code={code}
									items={recommendedResults}
								/>

								{canLoadMoreRecommended ? (
									<Button
										type="outlined"
										color="primary"
										onClick={loadMoreRecommended}>
										{t('akce.nacist-dalsich-10')}
									</Button>
								) : null}
							</>
						)}

						{!isFetching && numberOfResults > 0 && (
							<>
								<SearchGridHeader>
									<p>{t('vyhledavani-pocet', { count: numberOfResults })}</p>

									<SortDropdown
										code={code}
										isSortedAlphabetically={isSortedAlphabetically}
									/>
								</SearchGridHeader>
								<SearchResultsGrid
									code={code}
									items={results}
								/>
							</>
						)}

						{isFetching && <Skeleton code={code} />}

						{numberOfResults > filter.pageSize ? (
							<footer className="mt-6">
								<Button
									type="outlined"
									color="primary"
									onClick={loadMoreResults}>
									{t('akce.nacist-dalsich-10')}
								</Button>
							</footer>
						) : null}
					</LayoutGap>
				</GovLayoutColumn>
			</GovLayout>
		</LayoutGap>
	);
};
